import {
    StepState,
    stepStartState,
    stepCloneState,
    stepDevcontainerState,
    stepAutomationsState,
} from "@/components/environments/environment-start-steps-state";
import type { AgentMessage } from "@/hooks/use-stream-conversation";
import type { PlainEnvironment } from "@/queries/environment-queries";
import { useMemo } from "react";

import type { InjectionSpec } from "@/hooks/use-injected-messages";
import type { WelcomeAnchor } from "@/hooks/use-welcome-anchors";

export type InjectedMessageSpec = InjectionSpec & {
    anchorStartCount: number;
};

type LifecycleStep = {
    id: string;
    runningTexts: string[];
    doneText: string;
    getState: (env: PlainEnvironment) => StepState;
};

const LIFECYCLE_STEPS: LifecycleStep[] = [
    {
        id: "machine",
        runningTexts: ["I'm setting up your environment now. This usually takes under a minute."],
        doneText: "✅ Your environment machine is ready.",
        getState: stepStartState,
    },
    {
        id: "clone",
        runningTexts: [
            "I'm cloning your repository and getting dependencies installed.",
            "While that runs, I'm scanning your project to understand the languages, frameworks, and build tools you're using.",
            "I'm reading through your configuration files to get a sense of how everything is wired together.",
            "I looked for an AGENTS.md in your repo root — if you add one, I'll use it to pick up project-specific context automatically.",
            "I'm indexing your source files now so I can quickly navigate and search across the codebase when you need me to.",
            "I'm mapping out the module boundaries and key entry points so I know where things live.",
        ],
        doneText: "✅ Repository cloned and project indexed.",
        getState: stepCloneState,
    },
    {
        id: "integrations",
        runningTexts: [
            "I'm checking what integrations are available for your organization.",
            "Integrations are enabled ✅ If you connect your personal accounts in Settings → Integrations, I'll be able to work with Linear, Slack, GitHub, and more on your behalf.",
        ],
        doneText: "✅ Integrations are enabled and ready to use.",
        getState: stepAutomationsState,
    },
    {
        id: "devcontainer",
        runningTexts: [
            "I'm building the dev container for your project. The first build takes a bit longer, but future starts will be much faster.",
        ],
        doneText: "✅ Dev container is running. Your tools and dependencies are ready.",
        getState: (env) => stepDevcontainerState(env).state,
    },
];

const READY_TEXTS: string[] = [
    "Your environment is ready. I'm reading through the codebase now to understand your project.",
    "I'm reviewing the project structure, dependencies, and build configuration.",
    "Almost there — just putting together my initial assessment.",
];

const WELCOME_BACK_MESSAGES: Record<number, string[]> = {
    2: [
        "Welcome back — I'm spinning up your environment now.",
        "By the way, if you create a Project for this repo, I can use prebuilds to get your environment ready faster next time.",
        "With a Project, I'll automatically rebuild your dev container whenever your repo changes — so you won't have to wait.",
    ],
    3: [
        "Starting up your environment — this should be quick.",
        "Here's something that might save you time: you can set up Automations so I run tasks like linting or tests automatically whenever your environment starts.",
        'Also, you can work with me from VS Code Desktop or JetBrains if you prefer — you can open your repo in VS Code by clicking on the "Open VS Code" button found in the top right hand corner.',
    ],
};

export const CONTENT_STORAGE_PREFIX = "WELCOME_CONTENT_";

function getStoredContent(anchorKey: string): string[] | null {
    try {
        const raw = localStorage.getItem(`${CONTENT_STORAGE_PREFIX}${anchorKey}`);
        if (raw) {
            return JSON.parse(raw) as string[];
        }
    } catch {
        // localStorage may be unavailable
    }
    return null;
}

function storeContent(anchorKey: string, texts: string[]) {
    try {
        localStorage.setItem(`${CONTENT_STORAGE_PREFIX}${anchorKey}`, JSON.stringify(texts));
    } catch {
        // localStorage may be unavailable
    }
}

/**
 * Collects all lifecycle texts from the environment state.
 * Returns the texts and whether all steps have completed.
 */
function collectLifecycleTexts(environment: PlainEnvironment): { texts: string[]; allDone: boolean } {
    const texts: string[] = [];
    let allDone = true;

    for (const step of LIFECYCLE_STEPS) {
        const state = step.getState(environment);

        if (state === StepState.Empty || state === StepState.Waiting) {
            if (state === StepState.Waiting) {
                allDone = false;
            }
            continue;
        }

        if (state === StepState.Running) {
            allDone = false;
            texts.push(...step.runningTexts);
            continue;
        }

        if (state === StepState.Success || state === StepState.Warning) {
            texts.push(...step.runningTexts, step.doneText);
            continue;
        }

        if (state === StepState.Failure) {
            allDone = false;
            texts.push(...step.runningTexts);
            continue;
        }
    }

    if (allDone && texts.length > 0) {
        texts.push(...READY_TEXTS);
    }

    return { texts, allDone };
}

function textsToMessage(texts: string[]): AgentMessage {
    return {
        id: -1,
        type: "agent",
        fragments: texts.map((text, i) => ({
            type: "text" as const,
            id: `injected-lifecycle-${i}`,
            content: text,
            completed: true,
        })),
        injected: true,
    };
}

/**
 * Produces injected messages based on start count and environment state.
 *
 * - count=1: one message per lifecycle step text, derived from environment state.
 * - count=2–3: one message per welcome-back tip.
 * - count≥4: empty array.
 *
 * All messages have `injected: true`. The component layer handles
 * progressive rendering.
 */
export function useWelcomeContent(
    anchors: WelcomeAnchor[],
    environment: PlainEnvironment | undefined,
): InjectedMessageSpec[] {
    return useMemo(() => {
        const specs: InjectedMessageSpec[] = [];

        for (const anchor of anchors) {
            const count = anchor.startCount;

            if (count === 1) {
                // Prefer saved content — once all lifecycle steps completed
                // and the content was persisted, use it instead of
                // re-resolving from live environment state.
                const stored = getStoredContent(anchor.key);
                if (stored) {
                    specs.push({ anchorKey: anchor.key, anchorStartCount: count, messages: [textsToMessage(stored)] });
                    continue;
                }

                if (!environment) {
                    continue;
                }

                const { texts, allDone } = collectLifecycleTexts(environment);
                if (texts.length === 0) {
                    continue;
                }

                if (allDone) {
                    storeContent(anchor.key, texts);
                }

                specs.push({ anchorKey: anchor.key, anchorStartCount: count, messages: [textsToMessage(texts)] });
                continue;
            }

            // count 2 or 3: static welcome-back tips
            const texts = WELCOME_BACK_MESSAGES[count];
            if (!texts) {
                continue;
            }

            const message: AgentMessage = {
                id: -count,
                type: "agent",
                fragments: texts.map((text, i) => ({
                    type: "text" as const,
                    id: `injected-welcome-${count}-${i}`,
                    content: text,
                    completed: true,
                })),
                injected: true,
            };

            specs.push({ anchorKey: anchor.key, anchorStartCount: count, messages: [message] });
        }

        return specs;
    }, [anchors, environment]);
}
